/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { AdjustPassPunchesRequest } from '../models/AdjustPassPunchesRequest';
import type { AdminBulkRedeemPassRequest } from '../models/AdminBulkRedeemPassRequest';
import type { AdminPassCreateDTO } from '../models/AdminPassCreateDTO';
import type { AdminReleaseScheduleRequest } from '../models/AdminReleaseScheduleRequest';
import type { AdminSwapPassRequest } from '../models/AdminSwapPassRequest';
import type { AdminUpdatePassScheduleRequest } from '../models/AdminUpdatePassScheduleRequest';
import type { CardUpdateLinkResponse } from '../models/CardUpdateLinkResponse';
import type { CommunicationTrackingEventOut } from '../models/CommunicationTrackingEventOut';
import type { CustomerCancelOrderRequest } from '../models/CustomerCancelOrderRequest';
import type { CustomerPassCreateOrderRequest } from '../models/CustomerPassCreateOrderRequest';
import type { EditPassDTO } from '../models/EditPassDTO';
import type { EventPassHoldersOut } from '../models/EventPassHoldersOut';
import type { OrderData } from '../models/OrderData';
import type { ParallelActivityItem } from '../models/ParallelActivityItem';
import type { PassBuyAgainRequest } from '../models/PassBuyAgainRequest';
import type { PassCreateDTO } from '../models/PassCreateDTO';
import type { PassLookupRequest } from '../models/PassLookupRequest';
import type { PassLookupResponse } from '../models/PassLookupResponse';
import type { PassOrderPageAvailabilityOut } from '../models/PassOrderPageAvailabilityOut';
import type { PassOut } from '../models/PassOut';
import type { PassPaymentStatus } from '../models/PassPaymentStatus';
import type { PassStatus } from '../models/PassStatus';
import type { PassType } from '../models/PassType';
import type { PausePassRequest } from '../models/PausePassRequest';
import type { TokenizationLinkRequest } from '../models/TokenizationLinkRequest';
import type { TokenizationLinkResponse } from '../models/TokenizationLinkResponse';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class PassService {
  /**
   * Create Pass
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static createPass(requestBody: PassCreateDTO): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Edit Pass
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static editPass(requestBody: EditPassDTO): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/pass',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Archive Pass
   * @param passId
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static archivePass(passId: string): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/pass',
      query: {
        pass_id: passId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Admin Pass
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static createAdminPass(requestBody: AdminPassCreateDTO): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/admin',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Pass By Id
   * @param passId
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static getPassById(passId: string): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/pass/by_id',
      query: {
        pass_id: passId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Passes
   * @param page Page number
   * @param perPage Items per page
   * @param search Search value
   * @param passTypes Filter by pass types
   * @param statuses List of pass statuses
   * @param paymentStatuses List of payment statuses
   * @param orderPageIds Filter by pass template ids
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static getPasses(
    page: number = 1,
    perPage: number = 10,
    search?: string,
    passTypes?: Array<PassType>,
    statuses?: Array<PassStatus>,
    paymentStatuses?: Array<PassPaymentStatus>,
    orderPageIds?: Array<string>,
  ): CancelablePromise<Array<PassOut>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/pass/list',
      query: {
        page: page,
        per_page: perPage,
        search: search,
        pass_types: passTypes,
        statuses: statuses,
        payment_statuses: paymentStatuses,
        order_page_ids: orderPageIds,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Renewal Candidate Passes
   * @param withinDays Days horizon for 'about to expire'
   * @param orderPageIds Filter by pass template ids
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static getRenewalCandidatePasses(
    withinDays: number = 10,
    orderPageIds?: Array<string>,
  ): CancelablePromise<Array<PassOut>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/pass/renewal_candidates',
      query: {
        within_days: withinDays,
        order_page_ids: orderPageIds,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Customer Pass Lookup
   * @param requestBody
   * @returns PassLookupResponse Successful Response
   * @throws ApiError
   */
  public static customerPassLookup(requestBody: PassLookupRequest): CancelablePromise<PassLookupResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/customer/lookup',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Customer Pass Lookup By Code
   * @param code
   * @returns PassLookupResponse Successful Response
   * @throws ApiError
   */
  public static customerPassLookupByCode(code: string): CancelablePromise<PassLookupResponse> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/pass/customer/lookup_by_code',
      query: {
        code: code,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Customer Cancel Order
   * @param requestBody
   * @returns any Successful Response
   * @throws ApiError
   */
  public static customerCancelOrder(requestBody: CustomerCancelOrderRequest): CancelablePromise<any> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/customer/cancel',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Customer Pass Create Order
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static customerPassCreateOrder(requestBody: CustomerPassCreateOrderRequest): CancelablePromise<OrderData> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/customer/create_order',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Customer Pass Buy Again
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static customerPassBuyAgain(requestBody: PassBuyAgainRequest): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/customer/buy_again',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Customer Parallel Activity
   * @param accountId
   * @param email
   * @param eventId
   * @returns ParallelActivityItem Successful Response
   * @throws ApiError
   */
  public static customerParallelActivity(
    accountId: string,
    email: string,
    eventId: string,
  ): CancelablePromise<Array<ParallelActivityItem>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/pass/customer/parallel_activity',
      query: {
        account_id: accountId,
        email: email,
        event_id: eventId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Admin Bulk Redeem Pass
   * @param requestBody
   * @returns OrderData Successful Response
   * @throws ApiError
   */
  public static adminBulkRedeemPass(requestBody: AdminBulkRedeemPassRequest): CancelablePromise<Array<OrderData>> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/admin/bulk_redeem',
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Pause Pass
   * @param passId
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static pausePass(passId: string, requestBody: PausePassRequest): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/{pass_id}/pause',
      path: {
        pass_id: passId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Resume Pass
   * @param passId
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static resumePass(passId: string): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/{pass_id}/resume',
      path: {
        pass_id: passId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Reactivate Pass
   * @param passId
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static reactivatePass(passId: string): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/{pass_id}/reactivate',
      path: {
        pass_id: passId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Card Update Link
   * @param passId
   * @returns CardUpdateLinkResponse Successful Response
   * @throws ApiError
   */
  public static createPassCardUpdateLink(passId: string): CancelablePromise<CardUpdateLinkResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/{pass_id}/card_update_link',
      path: {
        pass_id: passId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Update Pass Schedule
   * @param passId
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static updatePassSchedule(
    passId: string,
    requestBody: AdminUpdatePassScheduleRequest,
  ): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/pass/{pass_id}/schedule',
      path: {
        pass_id: passId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Release Pass Schedule
   * @param passId
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static releasePassSchedule(
    passId: string,
    requestBody: AdminReleaseScheduleRequest,
  ): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'PUT',
      url: '/api/pass/{pass_id}/schedule/release',
      path: {
        pass_id: passId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Adjust Pass Punches
   * @param passId
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static adjustPassPunches(passId: string, requestBody: AdjustPassPunchesRequest): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/{pass_id}/punches',
      path: {
        pass_id: passId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Swap Pass
   * @param passId
   * @param requestBody
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static swapPass(passId: string, requestBody: AdminSwapPassRequest): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/{pass_id}/swap',
      path: {
        pass_id: passId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Cancel Pending Pass Swap
   * @param passId
   * @returns PassOut Successful Response
   * @throws ApiError
   */
  public static cancelPendingPassSwap(passId: string): CancelablePromise<PassOut> {
    return __request(OpenAPI, {
      method: 'DELETE',
      url: '/api/pass/{pass_id}/swap',
      path: {
        pass_id: passId,
      },
      errors: {
        400: `Bad Request`,
        404: `Not Found`,
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Pass Communications Tracking
   * @param passId
   * @returns CommunicationTrackingEventOut Successful Response
   * @throws ApiError
   */
  public static getPassCommunicationsTracking(passId: string): CancelablePromise<Array<CommunicationTrackingEventOut>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/pass/communications-tracking',
      query: {
        pass_id: passId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Pass Availability
   * @param passOrderPageId
   * @param accountId
   * @returns PassOrderPageAvailabilityOut Successful Response
   * @throws ApiError
   */
  public static getPassAvailability(
    passOrderPageId: string,
    accountId: string,
  ): CancelablePromise<Array<PassOrderPageAvailabilityOut>> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/pass_order_page/{pass_order_page_id}/availability',
      path: {
        pass_order_page_id: passOrderPageId,
      },
      query: {
        account_id: accountId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Get Event Pass Holders
   * @param eventId
   * @returns EventPassHoldersOut Successful Response
   * @throws ApiError
   */
  public static getEventPassHolders(eventId: string): CancelablePromise<EventPassHoldersOut> {
    return __request(OpenAPI, {
      method: 'GET',
      url: '/api/event/{event_id}/pass_holders',
      path: {
        event_id: eventId,
      },
      errors: {
        422: `Validation Error`,
      },
    });
  }

  /**
   * Create Tokenization Link
   * @param passId
   * @param requestBody
   * @returns TokenizationLinkResponse Successful Response
   * @throws ApiError
   */
  public static createMembershipTokenizationLink(
    passId: string,
    requestBody: TokenizationLinkRequest,
  ): CancelablePromise<TokenizationLinkResponse> {
    return __request(OpenAPI, {
      method: 'POST',
      url: '/api/pass/{pass_id}/tokenization_link',
      path: {
        pass_id: passId,
      },
      body: requestBody,
      mediaType: 'application/json',
      errors: {
        422: `Validation Error`,
      },
    });
  }
}
